import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, fmtMoney, formatApiError } from "../lib/api";
import { ensurePushSubscription, pushSupported } from "../lib/push";

export default function PriceAlerts() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`alerts_page.${k}`, o);
  const DIRECTIONS = [
    { value: "below", label: tr("dir_falls_below", "Falls at or below") },
    { value: "above", label: tr("dir_rises_above", "Rises at or above") },
  ];
  const CHANNELS = [
    { value: "push", label: tr("ch_browser_push", "Browser push") },
    { value: "email", label: tr("ch_email", "Email (manual)") },
    { value: "both", label: tr("ch_both", "Push + email") },
  ];
  const [spot, setSpot] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    threshold_usd: "",
    direction: "below",
    channel: pushSupported() ? "push" : "email",
  });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const refresh = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/market/ucs-index"),
        api.get("/alerts/price"),
      ]);
      setSpot(s.data);
      setAlerts(a.data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("alerts refresh failed:", err);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const spotUsd = useMemo(() => Number(spot?.spot?.USD || 0), [spot]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const thr = parseFloat(form.threshold_usd);
    if (!isFinite(thr) || thr <= 0) {
      toast.error("Enter a valid USD threshold.");
      return;
    }
    setLoading(true);
    try {
      let push_subscription = null;
      if (form.channel === "push" || form.channel === "both") {
        push_subscription = await ensurePushSubscription();
        setPermission(Notification.permission);
      }
      await api.post("/alerts/price", {
        threshold_usd: thr,
        direction: form.direction,
        channel: form.channel,
        push_subscription,
      });
      toast.success("Price alert armed.");
      setForm({ ...form, threshold_usd: "" });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message || "Could not create alert.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/alerts/price/${id}`);
      toast.success("Alert removed.");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const handleTestPush = async () => {
    try {
      await ensurePushSubscription();
      setPermission(Notification.permission);
      await api.post("/alerts/test-push");
      toast.success("Test push sent. Check your notifications.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const handleRunNow = async () => {
    try {
      const { data } = await api.post("/alerts/run-mine");
      toast.success(
        `Scanned at $${data.current_spot_usd.toFixed(2)} — fired ${data.alerts_fired}, delivered ${data.push_delivered}.`
      );
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="label-tag mb-4">{tr("tag")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title")}</h1>
        <p className="text-ink2 mt-3 max-w-2xl">
          {tr("subtitle", "Set USD thresholds against the live BMV Global UCS Index spot. Web-Push notifications work on desktop and on PWA-installed mobile devices — no app store required.")}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-line bg-surface p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-tag">{tr("ucs_spot", "UCS Spot · USD")}</div>
              <div className="mono-num text-4xl mt-1" data-testid="alerts-spot-usd">
                {spotUsd ? `$${spotUsd.toFixed(2)}` : "—"}
              </div>
            </div>
            <div className="text-xs font-mono text-ink2 text-right">
              <div>{tr("change_24h", "24h change")}</div>
              <div className="mono-num text-ink mt-1">{spot ? `${spot.change_pct_24h}%` : "—"}</div>
            </div>
          </div>

          <form onSubmit={handleCreate} className="space-y-4" data-testid="alert-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="label-tag">{tr("direction")}</span>
                <select
                  data-testid="alert-direction"
                  value={form.direction}
                  onChange={(e) => setForm({ ...form, direction: e.target.value })}
                  className="mt-2 w-full bg-bg border border-line px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ink"
                >
                  {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-tag">{tr("threshold")}</span>
                <input
                  data-testid="alert-threshold"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={form.threshold_usd}
                  onChange={(e) => setForm({ ...form, threshold_usd: e.target.value })}
                  placeholder="e.g. 32.50"
                  className="mt-2 w-full bg-bg border border-line px-3 py-2 text-sm mono-num rounded-none focus:outline-none focus:border-ink"
                />
              </label>
              <label className="block">
                <span className="label-tag">{tr("channel", "Channel")}</span>
                <select
                  data-testid="alert-channel"
                  value={form.channel}
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="mt-2 w-full bg-bg border border-line px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ink"
                >
                  {CHANNELS.map((c) => (
                    <option key={c.value} value={c.value} disabled={c.value !== "email" && !pushSupported()}>
                      {c.label}{c.value !== "email" && !pushSupported() ? " · unsupported" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                data-testid="create-alert-btn"
                className="btn-primary !px-6 !py-2.5 !text-xs disabled:opacity-50"
              >
                {loading ? tr("creating") : tr("create")}
              </button>
              <button
                type="button"
                onClick={handleTestPush}
                data-testid="test-push-btn"
                className="btn-outline !px-5 !py-2.5 !text-xs"
                disabled={!pushSupported()}
              >
                {tr("send_test_push", "Send test push")}
              </button>
              <button
                type="button"
                onClick={handleRunNow}
                data-testid="run-now-btn"
                className="btn-outline !px-5 !py-2.5 !text-xs"
              >
                {tr("scan_now", "Scan now")}
              </button>
              <span className="text-xs font-mono text-ink2" data-testid="permission-state">
                {tr("notifications", "Notifications")}: {pushSupported() ? permission : "unsupported"}
              </span>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-5 border border-line bg-surface p-6">
          <div className="label-tag">{tr("how_it_works", "How it works")}</div>
          <ol className="mt-4 space-y-3 text-sm text-ink2 list-decimal list-inside">
            <li>{tr("hiw_1", "Choose a direction (above/below) and a USD price.")}</li>
            <li>{tr("hiw_2", "Allow notifications when prompted — we store the subscription, not your device.")}</li>
            <li>{tr("hiw_3", "The server polls the BMV UCS spot and fires a one-shot push when the threshold is crossed.")}</li>
            <li>{tr("hiw_4", "Use ‘Scan now’ to evaluate immediately against the current spot.")}</li>
          </ol>
          <div className="mt-6 border border-line p-4">
            <div className="label-tag">{tr("settlement_note_title", "Settlement note")}</div>
            <p className="text-xs text-ink2 mt-2">
              {tr("settlement_note_body", "Alerts trigger once. Re-arm after a hit by creating a new one. Reference: ucsindex.vercel.app.")}
            </p>
          </div>
        </aside>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-12">
        <div className="label-tag mb-3">{tr("active_alerts")}</div>
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>{tr("th_when")}</Th><Th>{tr("th_direction")}</Th><Th>{tr("th_threshold")}</Th><Th>{tr("channel", "Channel")}</Th><Th>{tr("th_status")}</Th><Th>{tr("distance", "Distance")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {alerts.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-ink2" data-testid="alerts-empty">{tr("no_alerts")}</td></tr>
              )}
              {alerts.map((a) => {
                const distancePct = spotUsd ? ((a.threshold_usd - spotUsd) / spotUsd) * 100 : 0;
                const distanceLabel = spotUsd ? `${distancePct >= 0 ? "+" : ""}${distancePct.toFixed(2)}%` : "—";
                return (
                  <tr key={a.id} className="border-b border-line/60" data-testid={`alert-${a.id}`}>
                    <Td className="text-ink2 mono-num text-xs">{new Date(a.created_at).toLocaleString()}</Td>
                    <Td>
                      <span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${a.direction === "below" ? "bg-muted text-ink" : "bg-leaf text-white"}`}>
                        {a.direction === "below" ? tr("below") : tr("above")}
                      </span>
                    </Td>
                    <Td className="mono-num">{fmtMoney(a.threshold_usd, "USD")}</Td>
                    <Td className="text-xs font-mono uppercase text-ink2">{a.channel}</Td>
                    <Td>
                      {a.triggered_at ? (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase bg-leaf text-white">{tr("st_triggered")}</span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase border border-line">{tr("st_active")}</span>
                      )}
                    </Td>
                    <Td className="mono-num text-xs">{distanceLabel}</Td>
                    <Td>
                      <button
                        onClick={() => handleDelete(a.id)}
                        className="text-xs font-mono uppercase tracking-wider text-ink2 hover:text-ink underline underline-offset-4"
                        data-testid={`delete-alert-${a.id}`}
                      >
                        {tr("delete")}
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[11px] font-sans tracking-[0.2em] uppercase text-ink2">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
